import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { cloneDeep } from "lodash-es";
import { FullSmartFormCategory, SmartFormLogic, SmartFormLogicAction } from '../../../../../swaggerServices';


@Component({
    selector: 'app-smart-form-logic-editor',
    templateUrl: './smart-form-logic-component.html',
    styleUrls: ['./smart-form-logic-component.scss'],
    providers: [
        {
            provide: TRANSLOCO_SCOPE,
            useValue: { scope: 'smart-forms', alias: 'smartForms' }
        }
    ],
    standalone: false
})
export class SmartFormLogicEditor {
  @Input() smartFormCategories: Array<FullSmartFormCategory> = []
  @Input() existingLogicNode: SmartFormLogic & { localIndex: number };
  @Output() onSave = new EventEmitter();
  @Output() onCancel = new EventEmitter();
  @Output() onDelete = new EventEmitter();

  conditions: Array<SmartFormLogic> = [
    {
      questionId: null,
      operator: null,
      value: '',
    },
  ];

  operators: { [key in SmartFormLogic.OperatorEnum ]: String } = {
    equals: 'equals',
    notEquals: "doesn't equal",
    contains: 'contains',
    greaterThan: 'is greater than',
    lessThan: 'is less than',
    answered: 'is answered',
  };

  actions: SmartFormLogicAction[] = [];

  searchText = "";
  actionTypes = ["show", "mandatory"] // TODO: Create *ngFor to iterate through these to correct them

  showVisible = false;
  mandatoryVisible = false;
  validSaveCondition = false;

  questions: Array<any> = []

  ngOnInit() {
    this.questions = []
    this.smartFormCategories = cloneDeep(this.smartFormCategories);

    if (this.existingLogicNode != null) {
      this.loadExistingLogicData();
    }

    for (let i = 0; i < this.smartFormCategories.length; i++) {
      const category = this.smartFormCategories[i];
      if (this.existingLogicNode != null) {
        this.actionTypes.forEach(action => {
          category[action] = this.existingLogicNode.actions.some(val => val.entityId == category.id && val[action] == true)
        });
      }
      else {
        this.actionTypes.forEach(action => category[action] = false);
      }
      for (let j = 0; j < category.smartFormSubCategories.length; j++) {
        const subCategory = category.smartFormSubCategories[j];

        if (this.existingLogicNode != null) {
          this.actionTypes.forEach(action => {
            subCategory[action] = this.existingLogicNode.actions.some(val => val.entityId == subCategory.id && val[action] == true)
          });
        }
        else {
          this.actionTypes.forEach(action => subCategory[action] = false);
        }
        for (let k = 0; k < subCategory.smartFormQuestions.length; k++) {
          const question = subCategory.smartFormQuestions[k];

          if (this.existingLogicNode != null) {
            this.actionTypes.forEach(action => {
              question[action] = this.existingLogicNode.actions.some(val => val.entityId == question.questionId && val[action] == true)
            });
          }
          else {
            this.actionTypes.forEach(action => question[action] = false);
          }
          this.questions.push(question);
        }
      }
    }
  }

  checkIfDataIsValid() {
    if (!this.conditions?.length) {
      return false;
    }
    else for (let i = 0; i < this.conditions.length; i++) {
      const c = this.conditions[i];
      if (!c.questionId || !c.operator) {
        return false;
      }
      if ((!c.value || !c.value.length) && c.operator !== 'answered') {
        return false;
      }
    }

    if (this.getActionsObj().length === 0) {
      return false;
    }

    return true;
  }

  cancel() {
    this.onCancel.emit();
  }

  deleteLogic() {
    if (this.existingLogicNode) {
      this.onDelete.emit();
    }
  }

  getKeysOf(obj) {
    return Object.keys(obj)
  }

  stopPropagation(event: Event) {
    event.stopPropagation();
  }

  loadExistingLogicData() {
    this.actions = this.existingLogicNode.actions;

    this.conditions = [{
      id: +this.existingLogicNode.id,
      questionId: +this.existingLogicNode.questionId,
      operator: this.existingLogicNode.operator,
      value: this.existingLogicNode.value,
      logicType: this.existingLogicNode.logicType,
    }]

    this.existingLogicNode.andChildren?.forEach(andChild => {
      this.conditions.push({
        id: +andChild.id,
        questionId: +andChild.questionId,
        operator: andChild.operator,
        value: andChild.value,
        logicType: andChild.logicType,
      });
    });

    this.existingLogicNode.orChildren?.forEach(orChild => {
      this.conditions.push({
        id: +orChild.id,
        questionId: +orChild.questionId,
        operator: orChild.operator,
        value: orChild.value,
        logicType: orChild.logicType,
      });
    });
  }

  getActionsObj() {
    const getActions = (item, entityName) => {
      let newAction: SmartFormLogicAction = {
        entityId: entityName == 'question' ? item.questionId : item.id,
        entity: entityName
      }
      let success = false;
      this.actionTypes.forEach(actionType => {
        if (item[actionType]) {
          newAction[actionType] = item[actionType]
          success = true;
        }
      })
      if (success) {
        return newAction;
      }
      else {
        return null;
      }
    }

    const actions = [];
    let newAction = null;
    this.smartFormCategories.forEach(category => {
      newAction = getActions(category, "smartFormCategory");
      if (newAction) {
        actions.push(newAction)
      }
      category.smartFormSubCategories.forEach(subCategory => {
        newAction = getActions(subCategory, "smartFormSubCategory");
        if (newAction) {
          actions.push(newAction)
        }
        subCategory.smartFormQuestions.forEach(question => {
          newAction = getActions(question, "question");
          if (newAction) {
            actions.push(newAction)
          }
        });
      });
    })
    return actions;
  }

  toggleSelection(item, field="show") {
    item[field] = !item[field];
  }

  onSearch() {
    const searchText = this.searchText.trim().toLowerCase();

    // * casting this as "any[]" as we are extending the object with "appearsInSearch"
    (this.smartFormCategories as any[]).forEach((category) => {
      category.appearsInSearch = (category.id + category.name).toLowerCase().includes(searchText);

      category.smartFormSubCategories.forEach((subCategory ) => {
        subCategory.appearsInSearch = category.appearsInSearch || (subCategory.id + subCategory.name).toLowerCase().includes(searchText);

        subCategory.smartFormQuestions.forEach((question) => {
          question.appearsInSearch = subCategory.appearsInSearch || (question.questionId + question.question).toLowerCase().includes(searchText);

        });

        if (!subCategory.appearsInSearch && subCategory.smartFormQuestions.some(val => val.appearsInSearch)) {
          subCategory.appearsInSearch = true;
        }
      });
      if (!category.appearsInSearch && category.smartFormSubCategories.some(val => val.appearsInSearch)) {
        category.appearsInSearch = true;
      }
    });
  }

  countItemsSelected(actionType = "show") {
    let result = 0;
    this.smartFormCategories?.forEach(category => {
      if (category[actionType]) {
        result++;
      }
      category.smartFormSubCategories?.forEach(subCategory => {
        if (subCategory[actionType]) {
          result++;
        }
        subCategory.smartFormQuestions?.forEach(question => {
          if (question[actionType]) {
            result++;
          }
        })
      })
    })

    return result;
  }

  getSelectedQuestionIds(actionType = "show") {
    const result = []
    this.smartFormCategories?.forEach(category => {
      category.smartFormSubCategories?.forEach(subCategory => {
        subCategory.smartFormQuestions?.forEach(question => {
          if (question[actionType] == true) {
            result.push(question.questionId);
          }
        })
      })
    })
    return result.length ? result : null;
  }

  addCondition() {
    const newCondition = {
      logicType: SmartFormLogic.LogicTypeEnum.And,
      value: '',
    };
    this.conditions.push(newCondition);
  }

  removeCondition(index) {
    const condition = this.conditions[index];
    if(!condition) {
      throw new Error(`Condition of index "${index}" could not be found`)
    }

    const children = this.conditions.filter(val => val.parentId === condition.id);
    let newParent;

    if (children?.some(val => val.logicType == "and")) {
      newParent = children.find(val => val.logicType == "and");
    }
    else if (children?.some(val => val.logicType == "or")) {
      newParent = children.find(val => val.logicType == "or");
    }
    else {
      if (children?.length)
        throw new Error(`Condition of Id "${condition.id}" has children without and/ or operators!`)
      if (condition.logicType == "root")
        throw new Error("Logic Nodes cannot have 0 conditions")

      this.conditions.splice(index, 1);
      return;
    }

    if (condition.logicType == "root") {
      newParent.logicType = "root"
      newParent.parentId = null;
    }

    for (let i = 0; i < children.length; i++) {
      const childElement = children[i];
      if (childElement.id == newParent.id) {
        continue;
      }

      childElement.parentId = newParent.id;
    }

    this.conditions.splice(index, 1);
  }

  validateLogicTree() {
    let rootExists = false;
    let errorExists = false;
    this.conditions.forEach(condition => {
      if (condition.logicType == "root") {
        rootExists = true;
      }
      else if (this.conditions.every(val => val.id != condition.parentId)) {
        errorExists = true;
      }
    });
    return rootExists && !errorExists;
  }

  isActionSelected(questionId, actionType) {
    const actionIndex = this.actions.findIndex((action) => action.entityId === questionId);
    return actionIndex > -1 && this.actions[actionIndex][actionType];
  }

  saveLogicData() {
    // Prepare the data for sending to the backend
    let parentNode;
    this.conditions?.forEach((condition, index) => {
      const newNode: any = {
        id: condition.id || -1,
        questionId: +condition.questionId || null,
        operator: condition.operator,
        value: condition.value,
        parentId: +(index === 0 ? null : this.conditions[0].id > 0 ? this.conditions[0].id : null) || null,
        logicType: index === 0 ? "root" : 'and',
      };


      switch (newNode.logicType) {
        case "root": { // index should also be 0
          newNode.actions = this.getActionsObj();
          newNode.andChildren = [];
          newNode.orChildren = [];
          parentNode = newNode;
          break;
        }
        case "and": {
          parentNode.andChildren.push(newNode)
          break;
        }
        case "or": {
          parentNode.orChildren.push(newNode)
          break;
        }
        default:
          break;
      }
    });

    if(parentNode == undefined) {
      return;
    }

    if(this.existingLogicNode != null) {
      parentNode.localIndex = this.existingLogicNode.localIndex
    }

    this.onSave.emit(parentNode);
    // Send the prepared data to the backend // TODO: Write this route on the backend
    // this.sendDataToBackend(logicNodes).subscribe((response) => {
    //     console.log('Data saved successfully:', response);
    //   },
    //   (error) => {
    //     console.error('Error saving data:', error);
    //   }
    // );
  }
}
